import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import Login from './pages/Login'
import SetPassword from './pages/SetPassword'
import Layout from './components/layout/Layout'
import Dashboard from './pages/Dashboard'
import Catalog from './pages/Catalog'
import Suppliers from './pages/Suppliers'
import PurchaseOrders from './pages/PurchaseOrders'
import PODetail from './pages/PODetail'
import WholesalePOs from './pages/WholesalePOs'
import WholesalePODetail from './pages/WholesalePODetail'
import Shipments from './pages/Shipments'
import ShipmentDetail from './pages/ShipmentDetail'
import Invoices from './pages/Invoices'
import Users from './pages/Users'
import Admin from './pages/Admin'
import Inventory from './pages/Inventory'
import ShopifySync from './pages/ShopifySync'
import LotImport from './pages/LotImport'
import AssemblyOrders from './pages/AssemblyOrders'
import SubscriptionBoxes from './pages/SubscriptionBoxes'
import Sales from './pages/Sales'
import PlanningInputs from './pages/PlanningInputs'
import Planner from './pages/Planner'
import InviteTeammate from './pages/InviteTeammate'
import Help from './pages/Help'
import { brandAdminHome } from './lib/brandAdmin'
import './App.css'

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>
  if (!user) return <Navigate to="/login" replace />
  return children
}

// Pages hidden from brand_admins: opening one by URL sends them to their landing page instead
function HiddenForBrandAdmin({ children }) {
  const { profile, activeBrand } = useAuth()
  if (profile?.role === 'brand_admin') return <Navigate to={brandAdminHome(activeBrand)} replace />
  return children
}

function AppRoutes() {
  const { user, loading, passwordResetMode } = useAuth()
  // Invite/recovery links must set a password first — this takes priority over
  // both the loading spinner and the normal authenticated routes. We render the
  // Set Password screen when EITHER the link was detected (hash/event) OR the
  // user landed on /set-password directly (reset emails redirect here).
  const onSetPasswordPath = typeof window !== 'undefined' && window.location.pathname === '/set-password'
  if (passwordResetMode || onSetPasswordPath) {
    return <Routes><Route path="*" element={<SetPassword />} /></Routes>
  }
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>
  if (!user) return <Routes><Route path="*" element={<Login />} /></Routes>
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<HiddenForBrandAdmin><Dashboard /></HiddenForBrandAdmin>} />
        <Route path="catalog" element={<Catalog />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="purchase-orders/:id" element={<PODetail />} />
        <Route path="wholesale-orders" element={<HiddenForBrandAdmin><WholesalePOs /></HiddenForBrandAdmin>} />
        <Route path="wholesale-orders/:id" element={<HiddenForBrandAdmin><WholesalePODetail /></HiddenForBrandAdmin>} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="shipments/:id" element={<ShipmentDetail />} />
        <Route path="invoices" element={<HiddenForBrandAdmin><Invoices /></HiddenForBrandAdmin>} />
        <Route path="users" element={<Users />} />
        <Route path="invite" element={<InviteTeammate />} />
        <Route path="admin" element={<Admin />} />
        <Route path="inventory" element={<Inventory key="inventory" />} />
        <Route path="transfer-orders" element={<Inventory key="transfers" only="transfers" />} />
        <Route path="locations" element={<Inventory key="locations" only="locations" />} />
        <Route path="assembly-orders" element={<HiddenForBrandAdmin><AssemblyOrders /></HiddenForBrandAdmin>} />
        <Route path="subscription-boxes" element={<SubscriptionBoxes />} />
        <Route path="sales" element={<HiddenForBrandAdmin><Sales /></HiddenForBrandAdmin>} />
        <Route path="planning-inputs" element={<PlanningInputs />} />
        <Route path="planner" element={<Planner />} />
        <Route path="shopify-sync" element={<ShopifySync />} />
        <Route path="lot-import" element={<LotImport />} />
        <Route path="help" element={<Help />} />
      </Route>
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}
